/**
 * The creator routes: `/@<username>` and `/@<username>/collections/<slug>`.
 *
 * react-router 6 treats only a whole path segment as a parameter, so the
 * pattern `/@:username` is the literal text `@:username` and matches no real
 * creator URL. The routes instead take the whole first segment as `:handle`
 * and render a page only when it is `@` followed by at least one character.
 * Any other first segment renders nothing, which is what an unmatched path
 * renders in `App.tsx` (it has no catch-all). Static first segments
 * (`/login`, `/explore`, …) outrank `:handle`, so they resolve as before.
 *
 * `App.tsx` and the tests both mount `creatorRoutes`, so the tests exercise
 * the app's real routes.
 */
import React from 'react';
import { Route, useParams } from 'react-router-dom';
import CreatorProfile from './pages/CreatorProfile';
import CollectionPage from './pages/CollectionPage';

/** The username a first path segment names, or null when it is not `@<username>`. */
export const creatorUsername = (handle: string | undefined): string | null =>
  handle && handle.length > 1 && handle.startsWith('@') ? handle.slice(1) : null;

const useCreatorUsername = (): string | null =>
  creatorUsername(useParams<{ handle: string }>().handle);

// Each page instance is keyed by its params, so a move to another creator or
// collection mounts a fresh page: no state from the previous one (data, an
// error, collections) can show under the new URL, and a late response to the
// old instance lands on an unmounted component, which React drops.
const CreatorProfileRoute: React.FC = () => {
  const username = useCreatorUsername();
  return username ? <CreatorProfile key={username} username={username} /> : null;
};

const CollectionRoute: React.FC = () => {
  const username = useCreatorUsername();
  const { slug } = useParams<{ slug: string }>();
  return username && slug
    ? <CollectionPage key={JSON.stringify([username, slug])} username={username} slug={slug} />
    : null;
};

export const CREATOR_ROUTE_PATH = '/:handle';
export const COLLECTION_ROUTE_PATH = 'collections/:slug';

export const creatorRoutes = (
  <Route path={CREATOR_ROUTE_PATH}>
    <Route index element={<CreatorProfileRoute />} />
    <Route path={COLLECTION_ROUTE_PATH} element={<CollectionRoute />} />
  </Route>
);
